import { v4 as uuidv4 } from 'uuid';
import { attributeTypes } from 'utils';
import { attributesList } from './constnats';
import { useFormBuilder } from '../formContext';
import { SimpleTooltip } from 'components';
import { useDispatch } from 'react-redux';
import { documentVersionActions } from 'store';
import { isOwnedGoal } from '../../utils/goalAttributes';

export const Attributes = ({ fromBuilder }) => {
    const { formList, setFormList, activeSection, selectedGoalId } = useFormBuilder();
    const dispatch = useDispatch();
    const sectionIndex = (activeSection || 1) - 1;
    const currentSection = formList?.sections[sectionIndex];
    // A picked goal that owns its fields takes the new field alone.
    const intoGoal = !!currentSection?.goalSection && !!selectedGoalId && isOwnedGoal(currentSection, selectedGoalId);
    const sectionAttributes = (intoGoal ? currentSection?.goals?.[selectedGoalId]?.attributes : currentSection?.attributes) || [];
    const hasTarget = sectionAttributes.some((a) => a?.type === attributeTypes.TARGET);

    const addAttribute = (attribute) => {
        if (!formList?.model) {
            return;
        }

        const newAttribute = {
            ...attribute,
            id: uuidv4(),
        };
        delete newAttribute.img;
        delete newAttribute.border;
        delete newAttribute.dateTypesList;
        // Toolbar-only display name — the block keeps its own renameable `label`.
        delete newAttribute.displayLabel;

        const sectionIdx = (activeSection || 1) - 1;
        const section = formList?.sections?.[sectionIdx];

        setFormList((prevState) => {
            const idx = (activeSection || 1) - 1;
            const cur = prevState.sections[idx];
            const append = (list) => [...(list || []), { ...newAttribute, properties: { ...(newAttribute.properties || {}) } }];
            let updatedSection;
            if (intoGoal) {
                const goal = cur.goals?.[selectedGoalId] || {};
                updatedSection = { ...cur, goals: { ...cur.goals, [selectedGoalId]: { ...goal, attributes: append(goal.attributes) } } };
            } else {
                // No goal picked: the section's fields and every goal that owns its own.
                const goals = cur.goals
                    ? Object.fromEntries(
                          Object.entries(cur.goals).map(([gid, goal]) => [
                              gid,
                              goal?.ownAttributes ? { ...goal, attributes: append(goal.attributes) } : goal,
                          ]),
                      )
                    : cur.goals;
                updatedSection = { ...cur, attributes: append(cur.attributes), ...(goals ? { goals } : {}) };
            }
            const newSections = [...prevState.sections];
            newSections[idx] = updatedSection;
            return { ...prevState, sections: newSections };
        });

        // Live-capture the structural ADD for version history (content edits to
        // the new block consolidate into this same ADD on the backend). A field
        // added to one goal is not a section field, so it is not captured.
        if (formList?.id && section?.id && !intoGoal) {
            dispatch(
                documentVersionActions.captureDocumentChanges({
                    resourceModel: 'FORM',
                    resourceId: formList.id,
                    changes: [
                        {
                            changeType: 'ADD',
                            target: {
                                entity: 'ATTRIBUTE',
                                sectionId: section.id,
                                attributeId: newAttribute.id,
                                label: `${activeSection || 1}.${section.name || ''}`,
                            },
                            after: newAttribute,
                        },
                    ],
                }),
            );
        }
    };

    // Rebuilt on every render on purpose — each entry carries freshly generated
    // ids, so the block added on the next click never shares them with the last.
    const filteredAttributes = attributesList(formList?.model)?.filter((attr) => {
        if (attr.name === 'target' || attr?.name === 'graph') {
            if (!currentSection?.goalSection) return false;
            // Only one TARGET per section
            if (attr.name === 'target' && hasTarget) return false;
            // Hide TARGET in form builder mode
            if (attr.name === 'target' && fromBuilder) return false;
        }
        return true;
    });

    return (
        <div className="form-attributes-wrapper">
            {formList?.model ? (
                <div className="flex-align-center" style={{ gap: 4, opacity: formList?.model ? 1 : 0.5 }}>
                    {filteredAttributes?.map((i, j) => (
                        <div key={j} onClick={() => addAttribute(i)} className="flex-align-center">
                            {i?.border && <div className="form-attributes-item-border" />}
                            <div key={j} className="form-attributes-item-box">
                                <div key={j} className="form-attributes-item-image-box">
                                    <img src={i?.img} alt="icon" />
                                </div>
                                <p>{i?.displayLabel || i?.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <SimpleTooltip title={'Please select Use Case to enable adding attributes'} placement="top">
                    <div className="flex-align-center" style={{ gap: 4, opacity: formList?.model ? 1 : 0.5 }}>
                        {filteredAttributes?.map((i, j) => (
                            <div key={j} className="flex-align-center">
                                {i?.border && <div className="form-attributes-item-border" />}
                                <div key={j} className="form-attributes-item-box">
                                    <div key={j} className="form-attributes-item-image-box">
                                        <img src={i?.img} alt="icon" />
                                    </div>
                                    <p>{i?.displayLabel || i?.label}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </SimpleTooltip>
            )}
        </div>
    );
};
